var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { KRSnackbar } from '../../snackbar/snackbar.js';
/**
 * A thin wrapper that turns a group of krubble form fields into a single autosave unit.
 *
 * Slot krubble fields (kr-text-field, kr-textarea-field, kr-select-field, kr-checkbox,
 * kr-date-picker, ...) inside it, each with a `name`, and hand the form a `save` function:
 *
 *   <kr-form .save=${this.savePage}>
 *     <kr-text-field name="seoTitleTag" .value=${page.seoTitleTag}></kr-text-field>
 *   </kr-form>
 *
 *   savePage = (changes: Record<string, unknown>) => {
 *     return KRHttp.fetch({ url, method: 'PATCH', body: changes })
 *       .then(response => response.json())
 *       .then(result => { this.page = result.data; });
 *   };
 *
 * When a field commits a change (blur for text, selection for the rest) it bubbles a composed
 * `change`. The form listens for it and runs a save with only the fields the user actually changed
 * (the dirty ones). The form never makes the request itself — `save` owns it and must return a
 * promise that resolves on success and REJECTS on failure, because the form uses the outcome to
 * settle the fields' dirty state and to report the error. On a rejection the form shows an error
 * snackbar: the platform message from the Response body ({ errors: [...] }) when KRHttp rejected
 * with one, "Save failed." otherwise. So `save` doesn't need a catch — let the rejection through.
 *
 * Saves are sequenced: one runs at a time and a change that lands while a save is in flight queues
 * the next run, which picks up whatever is dirty when it starts. So two quick edits can't produce
 * two concurrent requests that resolve out of order, and a queued run is skipped if the previous
 * one already sent everything.
 *
 * Field state through a save (see each field's markSaving/markSaved/markSaveFailed):
 *   - At the start of a run the collected fields are marked `saving` and their `dirty` flag is
 *     cleared, so a further edit during the request is tracked as new.
 *   - On success `saving` clears. A field edited mid-flight is still dirty and goes out next run.
 *   - On failure `saving` clears and `dirty` is restored so the next change retries the edit.
 *   - While dirty, a field ignores external `value` sets (e.g. a parent re-binding from the server
 *     response), so an in-progress edit is never overwritten by a save that didn't include it. This
 *     is `save-mode="autosave"`: the form's default, inherited by every field in it. `save-mode="manual"`
 *     on the form turns it off for all of them, and a field's own `save-mode` overrides the form either
 *     way. A field outside any form is manual unless it sets `save-mode="autosave"` itself.
 *
 * Without a `save` function the form does nothing on its own; the field `change` still bubbles to
 * a `@change` bound on <kr-form> and getChanges()/hasChanges()/markPristine() let the consumer
 * drive the save manually.
 *
 * @fires change - the field's own composed change event, bubbled through the form.
 */
export class KRForm extends LitElement {
    constructor() {
        super(...arguments);
        // The tail of the save chain. Each change appends a run so saves execute strictly in order.
        this._saveQueue = Promise.resolve();
        /**
         * Persists the changed fields. Receives `{ name: value }` for the dirty fields and returns a
         * promise that resolves on success and rejects on failure. The form sequences calls so at most
         * one save is in flight at a time.
         */
        this.save = null;
        /**
         * Default save mode for the fields inside. In `autosave` mode a dirty field ignores external value
         * sets so a server response can't overwrite an unsaved edit; `manual` turns that off for the whole
         * form. A field's own `save-mode` overrides this either way.
         */
        this.saveMode = 'autosave';
        this._handleChange = () => {
            if (!this.save) {
                return;
            }
            // Queue one sequenced save. It collects the dirty fields as they stand when the run STARTS (not
            // when the change that queued it fired), so a run queued behind an in-flight save sends
            // everything edited since, and is a no-op if the previous run already covered it.
            this._saveQueue = this._saveQueue.then(() => {
                const fields = Array.from(this.querySelectorAll('.kr-form-field'))
                    .filter(field => field.dirty && field.name);
                if (!fields.length) {
                    return;
                }
                const changes = this.getChanges();
                for (const field of fields) {
                    field.markSaving?.();
                }
                // Promise.resolve().then(...) so a save function that throws synchronously is treated the
                // same as one that rejects, and the queue keeps going either way.
                return Promise.resolve()
                    .then(() => this.save(changes))
                    .then(() => {
                    for (const field of fields) {
                        field.markSaved?.();
                    }
                }, error => {
                    for (const field of fields) {
                        field.markSaveFailed?.();
                    }
                    // KRHttp rejects with the raw Response on a non-2xx, whose body carries the platform
                    // error. Anything else (a network failure, a throw inside `save`) has no body to read,
                    // so fall through with an empty error list and the fallback message shows.
                    if (error instanceof Response) {
                        console.error('kr-form save failed', error.status, error.url);
                        return error.json();
                    }
                    console.error('kr-form save failed', error);
                    return { errors: [] };
                })
                    .then(body => {
                    // body is undefined when the save succeeded (the success handler returns nothing).
                    if (!body) {
                        return;
                    }
                    KRSnackbar.show({
                        message: body.errors?.[0] || 'Save failed.',
                        type: 'error',
                    });
                }, 
                // error.json() itself rejected (malformed body). Still show something, and never let the
                // queue promise reject — a rejected tail would skip every save queued behind it.
                error => {
                    console.error('kr-form save failed with an unreadable response body', error);
                    KRSnackbar.show({
                        message: 'Save failed.',
                        type: 'error',
                    });
                });
            });
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('change', this._handleChange);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('change', this._handleChange);
    }
    // The outstanding changes as a { name: value } object. krubble form fields mark themselves with
    // the `.kr-form-field` class, so we select real controls (not just anything with a stray
    // attribute), then keep the ones the user changed (dirty). name/value are read as properties.
    getChanges() {
        const changes = {};
        for (const field of Array.from(this.querySelectorAll('.kr-form-field'))) {
            if (!field.dirty || !field.name) {
                continue;
            }
            // Return each field's value in its natural type so the payload matches the API's types:
            // checkboxes report their boolean `checked`; date pickers report a Date via valueAsDate;
            // number fields report valueAsNumber (a number, or null when empty); everything else reports
            // the string `value`.
            if ('checked' in field && typeof field.checked === 'boolean') {
                changes[field.name] = field.checked;
            }
            else if (field.tagName === 'KR-DATE-PICKER') {
                changes[field.name] = field.valueAsDate;
            }
            else if (field.type === 'number') {
                changes[field.name] = field.valueAsNumber;
            }
            else {
                changes[field.name] = field.value;
            }
        }
        return changes;
    }
    // True when at least one named field is dirty — i.e. getChanges() would return something. A
    // change event can reach kr-form from a slotted control that isn't a saveable field (e.g. an
    // unnamed file field, whose composed change bubbles through the form node), so a run is skipped
    // when nothing is dirty rather than firing a save with an empty body.
    hasChanges() {
        for (const field of Array.from(this.querySelectorAll('.kr-form-field'))) {
            if (field.dirty && field.name) {
                return true;
            }
        }
        return false;
    }
    // Resets every field's dirty flag. For consumers driving the save themselves (no `save` set).
    markPristine() {
        for (const field of Array.from(this.querySelectorAll('.kr-form-field'))) {
            field.markPristine?.();
        }
    }
    render() {
        return html `<slot></slot>`;
    }
}
KRForm.styles = css `
    :host {
      display: block;
    }
  `;
__decorate([
    property({ attribute: false })
], KRForm.prototype, "save", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRForm.prototype, "saveMode", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-form')) {
    customElements.define('kr-form', KRForm);
}
//# sourceMappingURL=form.js.map